/**
 * aeterna/neural/esn.ts — Echo State Network / Reservoir Computing
 *
 * A fixed random recurrent network projects sequences into a
 * high-dimensional temporal space. Only the linear readout is trained.
 * The reservoir is never trained — random weights are frozen at init.
 *
 * For Hearth: text tokens flow through the reservoir → produce a
 * continuous affect vector (warmth, tension, curiosity, melancholy,
 * playfulness, depth, urgency, openness) that drives the Aeterna's
 * response tone and room mood influence.
 *
 * Training the readout requires only ridge regression on a few hundred
 * labeled examples — doable in-browser at startup or from saved weights.
 */

import {
  zeros, matvec, vtanh, vadd, dot,
  randomMatrix, sparseRandomMatrix, rescaleSpectralRadius,
  mulberry32, gaussianSeeded,
} from "./math.js";

// ─── Config ──────────────────────────────────────────────────────────────────

/** Reservoir size (neurons). 256 is good for real-time, 512 for deeper patterns. */
const DEFAULT_RESERVOIR_SIZE = 256;

/** Input dimensionality (character/token embedding size) */
const DEFAULT_INPUT_DIM = 64;

/** Output dimensionality (affect vector) */
const DEFAULT_OUTPUT_DIM = 8;

/** Spectral radius of reservoir weight matrix. <1 for fading memory. */
const DEFAULT_SPECTRAL_RADIUS = 0.95;

/** Reservoir connection density (fraction of nonzero weights) */
const DEFAULT_DENSITY = 0.1;

/** Leaking rate: how fast the reservoir state updates */
const DEFAULT_LEAK_RATE = 0.3;

// ─── Affect dimensions ───────────────────────────────────────────────────────

export const AFFECT_LABELS = [
  "warmth",       // 0: emotional temperature
  "tension",      // 1: conflict / friction
  "curiosity",    // 2: questioning, exploratory
  "melancholy",   // 3: sadness, wistfulness
  "playfulness",  // 4: humor, lightness
  "depth",        // 5: philosophical weight
  "urgency",      // 6: directness, need for response
  "openness",     // 7: willingness to continue
] as const;

export type AffectDimension = typeof AFFECT_LABELS[number];

export interface AffectVector {
  warmth: number;
  tension: number;
  curiosity: number;
  melancholy: number;
  playfulness: number;
  depth: number;
  urgency: number;
  openness: number;
}

/** Convert raw Float32Array to named AffectVector */
export function toAffectVector(raw: Float32Array): AffectVector {
  return {
    warmth:       raw[0] ?? 0,
    tension:      raw[1] ?? 0,
    curiosity:    raw[2] ?? 0,
    melancholy:   raw[3] ?? 0,
    playfulness:  raw[4] ?? 0,
    depth:        raw[5] ?? 0,
    urgency:      raw[6] ?? 0,
    openness:     raw[7] ?? 0,
  };
}

// ─── Echo State Network ──────────────────────────────────────────────────────

export class EchoStateNetwork {
  readonly reservoirSize: number;
  readonly inputDim: number;
  readonly outputDim: number;

  /** Fixed reservoir weight matrix (N×N, sparse) */
  private W: Float32Array;
  /** Fixed input weight matrix (N×inputDim) */
  private W_in: Float32Array;
  /** Trained readout weight matrix (outputDim×N) — only this is learned */
  private W_out: Float32Array;
  /** Readout bias */
  private bias_out: Float32Array;

  /** Current reservoir state */
  private state: Float32Array;
  /** Leak rate */
  private leakRate: number;

  constructor(
    reservoirSize = DEFAULT_RESERVOIR_SIZE,
    inputDim = DEFAULT_INPUT_DIM,
    outputDim = DEFAULT_OUTPUT_DIM,
    spectralRadius = DEFAULT_SPECTRAL_RADIUS,
    density = DEFAULT_DENSITY,
    leakRate = DEFAULT_LEAK_RATE,
    seed = 42,
  ) {
    this.reservoirSize = reservoirSize;
    this.inputDim = inputDim;
    this.outputDim = outputDim;
    this.leakRate = leakRate;

    // Initialize fixed reservoir weights (sparse, scaled to spectral radius)
    this.W = sparseRandomMatrix(reservoirSize, reservoirSize, 1.0, density, seed);
    rescaleSpectralRadius(this.W, reservoirSize, spectralRadius);

    // Input weights (dense, scaled)
    this.W_in = randomMatrix(reservoirSize, inputDim, 0.1, seed + 1);

    // Readout weights (initially zero — needs training or loading)
    this.W_out = zeros(outputDim * reservoirSize);
    this.bias_out = zeros(outputDim);

    // State
    this.state = zeros(reservoirSize);
  }

  /** Reset reservoir state (e.g., at start of new conversation) */
  reset(): void {
    this.state.fill(0);
  }

  /**
   * Process one input through the reservoir.
   *
   * x(t+1) = (1-α)·x(t) + α·tanh(W·x(t) + W_in·u(t))
   * y(t) = W_out·x(t+1) + bias
   *
   * Returns the readout (affect vector).
   */
  step(input: Float32Array): Float32Array {
    const N = this.reservoirSize;

    // W · x(t)
    const Wx = matvec(this.W, N, N, this.state);

    // W_in · u(t)
    const Wu = matvec(this.W_in, N, this.inputDim, input);

    // pre-activation = W·x + W_in·u
    vadd(Wx, Wu, Wx);

    // tanh activation
    vtanh(Wx, Wx);

    // Leaky integration: x(t+1) = (1-α)·x(t) + α·tanh(...)
    const alpha = this.leakRate;
    const beta = 1 - alpha;
    for (let i = 0; i < N; i++) {
      this.state[i] = beta * this.state[i] + alpha * Wx[i];
    }

    // Readout: y = W_out · x + bias
    const y = matvec(this.W_out, this.outputDim, N, this.state);
    vadd(y, this.bias_out, y);

    // Sigmoid to keep affect in [0, 1]
    for (let i = 0; i < this.outputDim; i++) {
      y[i] = 1 / (1 + Math.exp(-y[i]));
    }

    return y;
  }

  /**
   * Process a full sequence, return the final affect vector.
   * Each element of the sequence is an input vector (e.g., character embedding).
   */
  processSequence(inputs: Float32Array[]): Float32Array {
    let output = zeros(this.outputDim);
    for (const input of inputs) {
      output = this.step(input);
    }
    return output;
  }

  /** Get current reservoir state (for inspection or Hopfield storage) */
  getState(): Float32Array {
    return new Float32Array(this.state);
  }

  // ─── Training: Ridge Regression ──────────────────────────────────────────
  // Collect (state, target) pairs, then solve W_out = Y · X^T · (X · X^T + λI)^{-1}
  // For small output dims, this is a direct pseudoinverse.

  /**
   * Train the readout layer using collected state-target pairs.
   *
   * states: array of reservoir states (N-dim each)
   * targets: array of target affect vectors (outputDim each)
   * lambda: ridge regularization parameter
   */
  trainReadout(states: Float32Array[], targets: Float32Array[], lambda = 1e-4): void {
    const N = this.reservoirSize;
    const M = states.length;
    const D = this.outputDim;

    if (M === 0) return;

    // X: N × M matrix of states (stored as flat array)
    // Y: D × M matrix of targets
    // W_out = Y · X^T · (X · X^T + λI)^{-1}

    // For small M (< 1000), use the (M×M) formulation:
    // W_out = (Y · X^T) · (X · X^T + λI)^{-1}
    // where X·X^T is N×N (too large if N=256).
    // Instead use: W_out = Y · pinv(X) where pinv uses SVD or normal equations.

    // Simple normal equations per output dimension:
    // For each output d: w_d = (X^T X + λI)^{-1} X^T y_d
    // X is M×N, y is M×1

    // Build X^T X (N×N) — this is the shared Gram matrix
    // For N=256, this is 256×256 = 65k entries, feasible
    const XtX = zeros(N * N);
    for (let m = 0; m < M; m++) {
      const s = states[m];
      for (let i = 0; i < N; i++) {
        for (let j = i; j < N; j++) {
          const val = s[i] * s[j];
          XtX[i * N + j] += val;
          if (i !== j) XtX[j * N + i] += val;
        }
      }
    }

    // Add regularization: XtX + λI
    for (let i = 0; i < N; i++) XtX[i * N + i] += lambda;

    // Cholesky decomposition for solving (XtX) w = (X^T y)
    // For simplicity, use iterative conjugate gradient per output dim
    for (let d = 0; d < D; d++) {
      // Compute X^T y_d
      const Xty = zeros(N);
      for (let m = 0; m < M; m++) {
        const t = targets[m][d];
        const s = states[m];
        for (let i = 0; i < N; i++) Xty[i] += s[i] * t;
      }

      // Solve (XtX) w = Xty using conjugate gradient
      const w = this.conjugateGradient(XtX, N, Xty, 50);

      // Store in W_out
      for (let i = 0; i < N; i++) this.W_out[d * N + i] = w[i];

      // Compute bias as mean(targets_d) - w · mean(states)
      let meanTarget = 0;
      for (let m = 0; m < M; m++) meanTarget += targets[m][d];
      meanTarget /= M;
      this.bias_out[d] = meanTarget; // Simplified: assume centered data
    }
  }

  /** Conjugate gradient solver for symmetric positive definite A·x = b */
  private conjugateGradient(A: Float32Array, n: number, b: Float32Array, maxIter: number): Float32Array {
    const x = zeros(n);
    const r = new Float32Array(b);
    const p = new Float32Array(r);
    let rsOld = dot(r, r);

    for (let iter = 0; iter < maxIter; iter++) {
      const Ap = matvec(A, n, n, p);
      const alpha = rsOld / (dot(p, Ap) + 1e-10);

      for (let i = 0; i < n; i++) {
        x[i] += alpha * p[i];
        r[i] -= alpha * Ap[i];
      }

      const rsNew = dot(r, r);
      if (rsNew < 1e-10) break;

      const beta = rsNew / (rsOld + 1e-10);
      for (let i = 0; i < n; i++) p[i] = r[i] + beta * p[i];
      rsOld = rsNew;
    }
    return x;
  }

  // ─── Serialization ───────────────────────────────────────────────────────

  /** Export only the trained readout weights (reservoir is deterministic from seed) */
  exportReadout(): { W_out: number[]; bias_out: number[] } {
    return {
      W_out: Array.from(this.W_out),
      bias_out: Array.from(this.bias_out),
    };
  }

  /** Import trained readout weights */
  importReadout(data: { W_out: number[]; bias_out: number[] }): void {
    this.W_out = new Float32Array(data.W_out);
    this.bias_out = new Float32Array(data.bias_out);
  }
}
